import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import type { UseMutationResult } from '@tanstack/react-query';
import { useInitializeFM } from '../../../hooks/useInitializeFM';
import { CancelDirectDebitMangopayModalView } from './CancelDirectDebitMangopayModalView';

interface CancelDirectDebitMangopayModalProps {
  open: boolean;
  handleClose: () => void;
  useDeleteDirectDebitMandate: () => UseMutationResult<
    void,
    unknown,
    {
      paymentProcessorMode: 'live' | 'test';
      resident_id: string;
      tenant: string;
    }
  >;
  isFamilyMember?: boolean;
}

export const CancelDirectDebitMangopayModal: FC<
  CancelDirectDebitMangopayModalProps
> = ({
  open,
  handleClose,
  useDeleteDirectDebitMandate,
  isFamilyMember = false,
}) => {
  const [msg, setMsg] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const onDeleteMandateSuccess = useCallback(() => {
    enqueueSnackbar(t('mobile.family-member.mandate-deletion-success'), {
      variant: 'success',
    });
    handleClose();
  }, [enqueueSnackbar, handleClose, t]);

  const onDeleteMandateError = useCallback(() => {
    enqueueSnackbar(t('mobile.family-member.mandate-deletion-failure'), {
      variant: 'error',
    });
  }, [enqueueSnackbar, t]);

  const {
    mutateAsync: handleDirectDebitCancel,
    isPending,
    isSuccess,
  } = useDeleteDirectDebitMandate();

  const { selectedResident } = useInitializeFM();

  const handleSubmitRequest = async () => {
    try {
      await handleDirectDebitCancel({
        tenant: selectedResident?.tenant ?? '',
        resident_id: selectedResident?.residentId ?? '',
        paymentProcessorMode:
          selectedResident?.tenantPaymentProcessorMode ?? 'live',
      });
      onDeleteMandateSuccess();
    } catch {
      onDeleteMandateError();
    }
  };

  return (
    <CancelDirectDebitMangopayModalView
      open={open}
      handleClose={handleClose}
      isSuccess={isSuccess}
      isPending={isPending}
      isFamilyMember={isFamilyMember}
      msg={msg}
      onMsgChange={setMsg}
      onSubmitRequest={handleSubmitRequest}
    />
  );
};
